import { QueryObserverResult, useQuery, UseQueryOptions } from '@tanstack/react-query';
import type { Initiative } from '../initiative-client/initiative-client.type';
import type { ProcessAnalysisInitiativeChartData } from '../portfolio-client/portfolio-client.type';
import { fetchProjects, getInitiatives, getInitiativesCompletion } from './projects-client';
import type { ProjectsResponse } from './projects-client.type';

/**
 * Query manager for fetching a list of projects for the current user.
 *
 * @param {UseQueryOptions<ProjectsResponse[], Error, ProjectsResponse[]> & { organizationId?: string | null }} options UseQueryOptions and optional organizationId
 * @returns ProjectsResponse[]
 */
const useProjects = (
  options: UseQueryOptions<ProjectsResponse[], Error, ProjectsResponse[]> & {
    organizationId?: string | null;
  },
): {
  projects: ProjectsResponse[] | undefined;
  query: QueryObserverResult<ProjectsResponse[]>;
} => {
  const { organizationId, queryKey = ['projects', organizationId], ...rest } = options;

  const query = useQuery({
    queryKey,
    queryFn: () => fetchProjects(organizationId),
    enabled: !!organizationId,
    ...rest,
  });

  return { projects: query.data, query };
};

/**
 * Query manager for fetching a list of initiatives for the given portfolio
 *
 * @param {string} projectId project id to fetch initiative completions for
 * @param {UseQueryOptions<Initiative[]>} options UseQueryOptions
 * @returns a object of initiatives along with the query manager
 */
const useProjectInitiatives = (
  projectId: string,
  options?: UseQueryOptions<Initiative[]>,
): {
  initiatives: Initiative[] | undefined;
  query: QueryObserverResult<Initiative[]>;
} => {
  const query = useQuery({
    queryKey: ['projectInitiatives', projectId],
    queryFn: ({ signal }) => getInitiatives(projectId, signal),
    ...options,
  });

  return { initiatives: query.data, query };
};

/**
 * Query manager for fetching a list of initiative completions for the given portfolio
 *
 * @param projectId project id to fetch initiative completions for
 * @param startDate start date to filter the initiative completions
 * @param endDate end date to filter the initiative completions
 * @param options UseQueryOptions
 * @returns a object of initiative completions along with the query manager
 */
const useProjectInitiativesCompletions = (
  projectId: string,
  startDate: string | null,
  endDate: string | null,
  options?: Partial<UseQueryOptions<ProcessAnalysisInitiativeChartData>>,
): {
  initiativeCompletions: ProcessAnalysisInitiativeChartData | undefined;
  query: QueryObserverResult<ProcessAnalysisInitiativeChartData>;
} => {
  const query = useQuery({
    queryKey: ['projectInitiativesCompletions', projectId, startDate, endDate],
    queryFn: ({ signal }) => getInitiativesCompletion(projectId, startDate, endDate, signal),
    ...options,
  });

  return { initiativeCompletions: query.data, query };
};

export { useProjectInitiatives, useProjectInitiativesCompletions, useProjects };
